import { Backdrop, Box, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Snackbar, Alert } from "@mui/material";
import TmFullButton from "../../../common/buttons/TmFullButton";
import TmEmptyButton from "../../../common/buttons/TmEmptyButton";
import { FormProvider, useForm } from "react-hook-form";
import { TextFieldFormControl } from "../../../common/form/TextFieldFormControl";
import { CompanyDetailsDto } from "../../../../models/companies/companyDetailsDto";
import { useState } from "react";
import { companiesService } from "../../../../services/companiesService";
import ErrorAlert from "../../../common/ErrorAlert";
import Spinner from "../../../common/Spinner";
import { DefaultIndicatorsDto } from "../../../../models/medicalIndicators/defaultIndicatorsDto";

interface EditDefaultIndicatorsDialogProps {
    open: boolean;
    company: CompanyDetailsDto;
    onClose: () => void;
}

export const EditDefaultIndicatorsDialog: React.FC<EditDefaultIndicatorsDialogProps> = ({ open, company, onClose }) => {
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [updatedCount, setUpdatedCount] = useState(0);

    const methods = useForm<DefaultIndicatorsDto>({
        defaultValues: {
            upperPressureMin: '',
            upperPressureMax: '',
            lowerPressureMin: '',
            lowerPressureMax: '',
            pulseMin: '',
            pulseMax: '',
            temperatureMax: '',
        }
    });

    const { reset } = methods;

    const handleOpen = () => {
        reset({
            upperPressureMin: '',
            upperPressureMax: '',
            lowerPressureMin: '',
            lowerPressureMax: '',
            pulseMin: '',
            pulseMax: '',
            temperatureMax: '',
        });
    };

    const onSubmit = (data: DefaultIndicatorsDto) => {
        setLoading(true);
        setError('');

        const payload: DefaultIndicatorsDto = {
            upperPressureMin: data.upperPressureMin !== '' ? Number(data.upperPressureMin) : undefined,
            upperPressureMax: data.upperPressureMax !== '' ? Number(data.upperPressureMax) : undefined,
            lowerPressureMin: data.lowerPressureMin !== '' ? Number(data.lowerPressureMin) : undefined,
            lowerPressureMax: data.lowerPressureMax !== '' ? Number(data.lowerPressureMax) : undefined,
            pulseMin: data.pulseMin !== '' ? Number(data.pulseMin) : undefined,
            pulseMax: data.pulseMax !== '' ? Number(data.pulseMax) : undefined,
            temperatureMax: data.temperatureMax !== '' ? Number(data.temperatureMax) : undefined,
        };

        companiesService.setDefaultIndicators(company.id, payload)
            .then((res) => {
                setUpdatedCount(res.data.data);
                setSuccess(true);
                onClose();
            })
            .catch((e) => {
                const errorMsg = e.response?.data?.message ?? e.message;
                setError(errorMsg);
            })
            .finally(() => setLoading(false));
    };

    return (
        <Box>
            <Dialog
                open={open}
                onClose={onClose}
                TransitionProps={{ onEntered: handleOpen }}
                PaperProps={{ sx: { borderRadius: 3, width: 550 } }}
            >
                <DialogTitle variant="h6" component="h2" textAlign="center">
                    Мед. показатели по умолчанию для всех водителей
                </DialogTitle>
                {error && (
                    <ErrorAlert
                        message={error}
                        onClose={() => setError('')}
                    />
                )}
                <DialogContent>
                    <FormProvider {...methods}>
                        <Grid container spacing={1}>
                            <Grid item xs={12} sx={{ mt: 1 }}>
                                <Box fontWeight="bold" mb={1}>Артериальное давление</Box>
                            </Grid>
                            <Grid item xs={6}>
                                <TextFieldFormControl
                                    name="upperPressureMin"
                                    label="Мин. арт. давление"
                                    type="number"
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextFieldFormControl
                                    name="upperPressureMax"
                                    label="Макс. арт. давление"
                                    type="number"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Box fontWeight="bold" mb={1}>Венозное давление</Box>
                            </Grid>
                            <Grid item xs={6}>
                                <TextFieldFormControl
                                    name="lowerPressureMin"
                                    label="Мин. вен. давление"
                                    type="number"
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextFieldFormControl
                                    name="lowerPressureMax"
                                    label="Макс. вен. давление"
                                    type="number"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Box fontWeight="bold" mb={1}>Пульс</Box>
                            </Grid>
                            <Grid item xs={6}>
                                <TextFieldFormControl
                                    name="pulseMin"
                                    label="Мин. пульс"
                                    type="number"
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextFieldFormControl
                                    name="pulseMax"
                                    label="Макс. пульс"
                                    type="number"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Box fontWeight="bold" mb={1}>Температура</Box>
                            </Grid>
                            <Grid item xs={6}>
                                <TextFieldFormControl
                                    name="temperatureMax"
                                    label="Макс. температура"
                                    type="number"
                                />
                            </Grid>
                        </Grid>
                    </FormProvider>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'space-between', mt: 1, mx: 2, mb: 3 }}>
                    <TmEmptyButton onClick={onClose} sx={{ flexGrow: 1 }}>
                        Отмена
                    </TmEmptyButton>
                    <TmFullButton onClick={methods.handleSubmit(onSubmit)} sx={{ flexGrow: 1 }}>
                        Применить ко всем водителям
                    </TmFullButton>
                </DialogActions>
            </Dialog>
            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }}
                open={loading}
            >
                <Spinner />
            </Backdrop>
            <Snackbar
                open={success}
                autoHideDuration={4000}
                onClose={() => setSuccess(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="success" onClose={() => setSuccess(false)}>
                    Показатели применены к {updatedCount} водителям
                </Alert>
            </Snackbar>
        </Box>
    );
};
